import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { analytics } from "./firebase";

import { mixpanelTrack } from "./mixpanel";


// Log levels
export enum LogLevel {
    DEBUG = 'debug',
    INFO = 'info',
    WARN = 'warn',
    ERROR = 'error',
    FATAL = 'fatal'
}

// User context interface
interface UserContext {
    userId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    userType?: string;
    subscriptionPlan?: string;
    [key: string]: any;
}

// Identify data interface (mirrors Mixpanel identify fields)
interface IdentifyData {
    colendiId?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
    [key: string]: any;
}

// Log entry interface
interface LogEntry {
    level: LogLevel;
    message: string;
    timestamp: number;
    userContext?: UserContext;
    metadata?: Record<string, any>;
    error?: Error;
    stackTrace?: string;
}

class FirebaseLogger {
    private userContext: UserContext = {};
    private isAnalyticsEnabled: boolean = true;
    private identifiedUserId?: string;

    constructor() {
        // Check if analytics is available
        try {
            if (analytics) {
                this.isAnalyticsEnabled = true;
            }
        } catch (error) {
            this.isAnalyticsEnabled = false;
            console.warn('Firebase Analytics not available:', error);
        }
    }

    /**
     * Set user context for all subsequent logs
     */
    setUserContext(context: UserContext): void {
        this.userContext = { ...this.userContext, ...context };

        if (this.isAnalyticsEnabled && analytics) {
            try {
                // Set user ID for Firebase Analytics
                if (context.userId) {
                    setUserId(analytics, context.userId);
                }

                // Set user properties
                const properties: Record<string, any> = {};
                if (context.email) properties.email = context.email;
                if (context.userType) properties.user_type = context.userType;
                if (context.subscriptionPlan) properties.subscription_plan = context.subscriptionPlan;

                if (Object.keys(properties).length > 0) {
                    setUserProperties(analytics, properties);
                }
            } catch (error) {
                console.error('Failed to set user properties:', error);
            }
        }
    }

    /**
     * Clear user context
     */
    clearUserContext(): void {
        this.userContext = {};
        if (this.isAnalyticsEnabled && analytics) {
            try {
                setUserId(analytics, null);
            } catch (error) {
                console.error('Failed to clear user ID:', error);
            }
        }
        this.identifiedUserId = undefined;
    }

    /**
     * Log a message with specified level
     */
    private log(level: LogLevel, message: string, metadata?: Record<string, any>, error?: Error): void {
        const logEntry: LogEntry = {
            level,
            message,
            timestamp: Date.now(),
            userContext: this.userContext,
            metadata,
            error,
            stackTrace: error?.stack
        };

        // Console logging with proper formatting
        this.logToConsole(logEntry);

        // Send to Firebase Analytics if enabled
        if (this.isAnalyticsEnabled) {
            this.logToFirebase(logEntry);
        }
    }

    /**
     * Format and log to console
     */
    private logToConsole(entry: LogEntry): void {
        const timestamp = new Date(entry.timestamp).toISOString();
        const prefix = `[${timestamp}] [${entry.level.toUpperCase()}]`;

        const consoleMethod = this.getConsoleMethod(entry.level);

        if (entry.error) {
            consoleMethod(`${prefix} ${entry.message}`, {
                error: entry.error,
                metadata: entry.metadata,
                userContext: entry.userContext
            });
        } else {
            consoleMethod(`${prefix} ${entry.message}`, {
                metadata: entry.metadata,
                userContext: entry.userContext
            });
        }
    }

    /**
     * Get appropriate console method for log level
     */
    private getConsoleMethod(level: LogLevel): (...args: any[]) => void {
        switch (level) {
            case LogLevel.DEBUG:
                return console.debug;
            case LogLevel.INFO:
                return console.info;
            case LogLevel.WARN:
                return console.warn;
            case LogLevel.ERROR:
            case LogLevel.FATAL:
                return console.error;
            default:
                return console.log;
        }
    }

    /**
     * Send log to Firebase Analytics
     */
    private logToFirebase(entry: LogEntry): void {
        if (!analytics) return;

        try {
            const eventName = this.getFirebaseEventName(entry.level);
            const parameters: Record<string, any> = {
                message: entry.message,
                level: entry.level,
                timestamp: entry.timestamp,
                ...entry.metadata
            };

            // Add error information if present
            if (entry.error) {
                parameters.error_message = entry.error.message;
                parameters.error_name = entry.error.name;
                if (entry.stackTrace) {
                    parameters.stack_trace = entry.stackTrace.substring(0, 100); // Limit length
                }
            }

            // Add user context
            if (entry.userContext) {
                Object.keys(entry.userContext).forEach(key => {
                    if (key !== 'userId') { // userId is set separately
                        parameters[`user_${key}`] = entry.userContext![key];
                    }
                });
            }

            logEvent(analytics, eventName, parameters);
        } catch (error) {
            console.error('Failed to log to Firebase Analytics:', error);
        }
    }

    /**
     * Get Firebase event name based on log level
     */
    private getFirebaseEventName(level: LogLevel): string {
        switch (level) {
            case LogLevel.DEBUG:
                return 'app_debug';
            case LogLevel.INFO:
                return 'app_info';
            case LogLevel.WARN:
                return 'app_warning';
            case LogLevel.ERROR:
                return 'app_error';
            case LogLevel.FATAL:
                return 'app_fatal_error';
            default:
                return 'app_log';
        }
    }

    // Public logging methods
    debug(message: string, metadata?: Record<string, any>): void {
        this.log(LogLevel.DEBUG, message, metadata);
    }

    info(message: string, metadata?: Record<string, any>): void {
        this.log(LogLevel.INFO, message, metadata);
    }

    warn(message: string, metadata?: Record<string, any>): void {
        this.log(LogLevel.WARN, message, metadata);
    }

    error(message: string, error?: Error, metadata?: Record<string, any>): void {
        this.log(LogLevel.ERROR, message, metadata, error);
    }

    fatal(message: string, error?: Error, metadata?: Record<string, any>): void {
        this.log(LogLevel.FATAL, message, metadata, error);
    }

    // Specialized logging methods
    logUserAction(action: string, details?: Record<string, any>): void {
        this.info(`User action: ${action}`, { action_type: 'user_interaction', ...details });
    }

    /**
     * Identify user in Firebase Analytics and cache context
     * Mirrors Mixpanel identify behavior
     */
    identifyUser(data: IdentifyData): void {
        if (!data) return;

        // Update local user context for subsequent logs
        const context: UserContext = {};
        if (data.colendiId) context.userId = data.colendiId;
        if (data.email) context.email = data.email;
        if (data.firstName) context.firstName = data.firstName;
        if (data.lastName) context.lastName = data.lastName;
        if (data.phoneNumber) context.phoneNumber = data.phoneNumber;
        this.setUserContext(context);

        if (!this.isAnalyticsEnabled || !analytics) {
            return;
        }

        try {
            // Set user ID if provided and changed
            if (data.colendiId && this.identifiedUserId !== data.colendiId) {
                setUserId(analytics, data.colendiId);
                this.identifiedUserId = data.colendiId;
            }

            // Set user properties
            const properties: Record<string, any> = {};
            if (data.firstName) properties.first_name = data.firstName;
            if (data.lastName) properties.last_name = data.lastName;
            if (data.phoneNumber) properties.phone_number = data.phoneNumber;
            if (data.email) properties.email = data.email;

            if (Object.keys(properties).length > 0) {
                setUserProperties(analytics, properties);
            }
        } catch (error) {
            console.error('Failed to identify user in Firebase Analytics:', error);
        }
    }

    logApiCall(endpoint: string, method: string, duration?: number, statusCode?: number): void {
        this.info(`API call: ${method} ${endpoint}`, {
            action_type: 'api_call',
            endpoint,
            method,
            duration,
            status_code: statusCode
        });
    }

    logApiError(endpoint: string, method: string, error: Error, statusCode?: number): void {
        this.error(`API error: ${method} ${endpoint}`, error, {
            action_type: 'api_error',
            endpoint,
            method,
            status_code: statusCode
        });
    }

    logPageView(pageName: string, metadata?: Record<string, any>): void {
        this.info(`Page view: ${pageName}`, {
            action_type: 'page_view',
            page_name: pageName,
            ...metadata
        });

        // Also log to Firebase Analytics as a page_view event
        if (this.isAnalyticsEnabled && analytics) {
            try {
                logEvent(analytics, 'page_view', {
                    page_title: pageName,
                    ...metadata
                });
            } catch (error) {
                console.error('Failed to log page view to Firebase:', error);
            }
        }
    }

    logBusinessEvent(eventName: string, metadata?: Record<string, any>): void {
        this.info(`Business event: ${eventName}`, {
            action_type: 'business_event',
            event_name: eventName,
            ...metadata
        });

        // Also log as custom event to Firebase Analytics
        if (this.isAnalyticsEnabled && analytics) {
            try {
                logEvent(analytics, eventName.toLowerCase().replace(/\s+/g, '_'), metadata);
            } catch (error) {
                console.error('Failed to log business event to Firebase:', error);
            }
        }
    }
}

// Create and export a singleton instance
export const firebaseLogger = new FirebaseLogger();

// Export convenience functions
export const logDebug = (message: string, metadata?: Record<string, any>) =>
    firebaseLogger.debug(message, metadata);

export const logInfo = (message: string, metadata?: Record<string, any>) =>
    firebaseLogger.info(message, metadata);

export const logWarn = (message: string, metadata?: Record<string, any>) =>
    firebaseLogger.warn(message, metadata);

export const logError = (message: string, error?: Error, metadata?: Record<string, any>) =>
    firebaseLogger.error(message, error, metadata);

export const logFatal = (message: string, error?: Error, metadata?: Record<string, any>) =>
    firebaseLogger.fatal(message, error, metadata);

export const logUserAction = (action: string, details?: Record<string, any>) =>
    firebaseLogger.logUserAction(action, details);

export const logApiCall = (endpoint: string, method: string, duration?: number, statusCode?: number) =>
    firebaseLogger.logApiCall(endpoint, method, duration, statusCode);

export const logApiError = (endpoint: string, method: string, error: Error, statusCode?: number) =>
    firebaseLogger.logApiError(endpoint, method, error, statusCode);

export const logPageView = (pageName: string, metadata?: Record<string, any>) =>
    firebaseLogger.logPageView(pageName, metadata);

export const logBusinessEvent = (eventName: string, metadata?: Record<string, any>) =>
    firebaseLogger.logBusinessEvent(eventName, metadata);

export const setUserContext = (context: UserContext) =>
    firebaseLogger.setUserContext(context);

export const clearUserContext = () =>
    firebaseLogger.clearUserContext();

// Identify helper to mirror Mixpanel identify usage
export const identifyUser = (data: IdentifyData) =>
    firebaseLogger.identifyUser(data);

// Simple function to send custom events directly to Firebase Analytics
export const sendEvent = (eventName: string, parameters?: Record<string, any>) => {
    mixpanelTrack(eventName, parameters);

    if (!analytics) {
        console.warn('Firebase Analytics not available');
        return;
    }

    try {
        // Clean event name (Firebase Analytics requires lowercase with underscores)
        const cleanEventName = eventName.toLowerCase().replace(/[^a-z0-9_]/g, '_');
        logEvent(analytics, cleanEventName, parameters);

        // Optional: Also log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`🔥 Firebase Event: ${cleanEventName}`, parameters);
        }
    } catch (error) {
        console.error('Failed to send Firebase event:', error);
    }
}; 